import React, { useState } from 'react';
import { Card, Row, Col, notification } from 'antd';
import { Tooltip, Menu, Dropdown, Colors } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled from 'styled-components/macro';
import { useUrl } from 'hooks/useUrls';
import mixpanel from 'utils/mixpanel-frontend';
import {
  SchemaCollection,
  useDeleteSchemaMutation,
  useGetDigitalTwinQuery,
} from 'types/dt-graphql-apollo-v2';
import { getDigitalTwinSchemaUrl } from 'utils/router-url';
import { useStatefulHistory } from 'hooks/history';
import { useAccessControl } from 'hooks/useAccessControl';
import { DeleteItemDialog } from 'components/Settings/DeleteItemDialog';
import { EditSchemaVersionDialog } from './EditSchemaVersionDialog';

type SchemaListTypes = {
  schemaCollection: SchemaCollection;
};

export const SchemaCard = ({ schemaCollection }: SchemaListTypes) => {
  const tenant = useUrl.Tenant();
  const digitalTwinId = useUrl.DigitalTwinId();
  const history = useStatefulHistory();
  const [deleteSchema] = useDeleteSchemaMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const hasAccess = useAccessControl();
  const canDeleteSchemaCollection = hasAccess(['Delete_SchemaCollection']);
  const canEditSchemaCollection = hasAccess(['Update_SchemaCollection']);

  const { refetch: refetchDigitalTwins } = useGetDigitalTwinQuery({
    variables: {
      id: digitalTwinId,
    },
  });

  const onOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    mixpanel.track('Opened schema in Project Management', {
      id: schemaCollection.externalId,
      name: schemaCollection.name,
    });
    history.pushStateful(
      `${getDigitalTwinSchemaUrl(
        tenant,
        digitalTwinId,
        schemaCollection.name
      )}/schema_view`
    );
  };

  const onRemove = async () => {
    try {
      await deleteSchema({ variables: { id: schemaCollection.externalId } });
      await refetchDigitalTwins();
      notification.success({
        message: `${schemaCollection.name} was removed`,
        description: `Successfully removed the schema named ${schemaCollection.name}`,
      });
      mixpanel.track(`Removed schema`, { id: schemaCollection.externalId });
    } catch (error: any) {
      mixpanel.track(`Could not remove schema`, {
        id: schemaCollection.externalId,
        error: error.toString(),
      });
      notification.error({
        message: `Could not remove schema`,
        description: error.toString(),
      });
    }
  };

  return (
    <StyledSchemaContainer key={schemaCollection.externalId}>
      <Row>
        <Col flex="262px" style={{ marginBottom: '2rem' }}>
          <StyledCard onClick={onOpen}>
            <StyledTitleTooltip
              disabled={schemaCollection.name.length < 15}
              content={schemaCollection.name}
            >
              <StyledTitleContainer>
                <StyledImage
                  onClick={onOpen}
                  src={`${process.env.PUBLIC_URL}/images/icons/schema.svg`}
                  alt="Create a schema"
                  width="22"
                />
                <StyledTitle onClick={onOpen}>
                  {schemaCollection.name}
                </StyledTitle>
                {(canDeleteSchemaCollection || canEditSchemaCollection) && (
                  <Dropdown
                    content={
                      <StyledContextMenu>
                        <StyledHeader>SETTINGS</StyledHeader>
                        {canEditSchemaCollection && (
                          <Menu.Item
                            onClick={() => {
                              setShowEditDialog(true);
                            }}
                            icon="Edit"
                            iconPlacement="left"
                          >
                            Edit schema version
                          </Menu.Item>
                        )}
                        {canDeleteSchemaCollection && (
                          <Menu.Item
                            onClick={() => {
                              setShowDeleteDialog(true);
                            }}
                            icon="Delete"
                            iconPlacement="left"
                          >
                            Delete schema
                          </Menu.Item>
                        )}
                      </StyledContextMenu>
                    }
                  >
                    <StyledIcon type="EllipsisVertical" size={18} />
                  </Dropdown>
                )}
              </StyledTitleContainer>
            </StyledTitleTooltip>
          </StyledCard>
        </Col>
      </Row>
      <div aria-hidden="true" role="link">
        {showDeleteDialog && (
          <DeleteItemDialog
            itemType="schema"
            onClose={() => setShowDeleteDialog(false)}
            onDelete={() => onRemove()}
            actionType="Delete"
          />
        )}
        {showEditDialog && (
          <EditSchemaVersionDialog
            schemaCollection={schemaCollection}
            onSuccess={() => {
              setShowEditDialog(false);
              refetchDigitalTwins();
            }}
            onClose={() => setShowEditDialog(false)}
          />
        )}
      </div>
    </StyledSchemaContainer>
  );
};

const StyledContextMenu = styled(Menu)`
  padding: 15px;
`;

const StyledHeader = styled(Menu.Header)`
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.3px;
`;

const StyledIcon = styled(Icon)`
  display: inline-flex;
  height: 2rem;
  cursor: pointer;
  align-items: center;

  &:hover {
    color: var(--cogs-primary);
  }
`;

const StyledSchemaContainer = styled.div`
  width: 250px;
  height: 150px;
  margin-bottom: 1rem;
`;

const StyledImage = styled.img`
  margin: auto 1rem 1rem auto;
`;

const StyledTitleContainer = styled.div`
  display: flex;
`;

const StyledTitle = styled.div`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 1.2rem;
  font-weight: 600;
`;

const StyledCard = styled(Card)`
  box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.1);
  transition: all 250ms linear;
  cursor: pointer;
  height: 150px;
  border-radius: 5px;

  .ant-card-body {
    padding: 1rem 1.5rem 0.5rem 1.5rem;
  }

  &:hover {
    background-color: var(--cogs-greyscale-grey1);
  }
`;

const StyledTitleTooltip = styled(Tooltip)`
  flex: 1;
  width: 300px;
  background-color: var(--cogs-greyscale-grey2);
  color: var(--cogs-black);
`;
